import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store/store";
import { InterfaceStory } from "../../sdk/client/model/BoxSdk/UIModel/box_interface";
import { loadBoxInterface } from "../../network/actions";
export interface StoriesContentItem {
  path: string
  duration: number
  extension?: string
}
export interface StoriesItem {
  id: number;
  name: string;
  image: string;
  videos: StoriesContentItem[];
}

export interface StoriesState {
  stories: StoriesItem[];
  currentStoryId: number | null;
  currentStory: StoriesItem | null;
  currentVideoIndex: number;
  videoDuration: number;
  videoProgress: number;
}

function mapInterfaceStoriesToStories(items: InterfaceStory[]): StoriesItem[] {
  return items
    .filter(s => s.storiesFile.length > 0)
    .sort((a, b) => a.index - b.index)
    .map(s => ({
      id: s.id,
      name: s.title,
      image: s.image_path,
      videos: [...s.storiesFile]
        .sort((a, b) => a.index - b.index)
        .map(f => ({ path: f.path, duration: f.duration, extension: f.extension })),
    }));
}

export function getStoriesContentType(item: StoriesContentItem): 'video' | 'image' {
  if (item.extension) {
    return item.extension.toLowerCase() === 'mp4' ? 'video' : 'image';
  }
  const suffix = item.path.split('.').pop()?.toLowerCase();
  if (suffix && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(suffix)) {
    return 'image';
  }
  return 'video';
}

const initialState: StoriesState = {
  stories: [],
  currentStoryId: null,
  currentStory: null,
  currentVideoIndex: 0,
  videoProgress: 0,
  videoDuration: 0,
};

export const storiesState = createSlice({
  name: "StoriesState",
  initialState,
  reducers: {
    showStory: (state, action: PayloadAction<number>) => {
      state.currentStoryId = action.payload;
      state.currentStory = state.stories.find(story => story.id === action.payload) || null;
      state.videoProgress = 0; // Начинаем с нулевого прогресса
      state.currentVideoIndex = 0;
    },
    onCloseStory: (state) => {
      state.currentStoryId = null;
      state.currentStory = null;
      state.videoProgress = 0;
      state.currentVideoIndex = 0;
    },
    updateVideoProgress: (state, action: PayloadAction<VideoProgress>) => {
      state.videoProgress = action.payload.progress;
      state.videoDuration = action.payload.duration;
    },
    incrementVideoIndex: (state, action: PayloadAction<number>) => {
      if (state.currentVideoIndex != action.payload) {
        return
      }
      if (state.currentStory && state.currentStory.videos) {
        if (state.currentVideoIndex < state.currentStory.videos.length - 1) {
          state.currentVideoIndex += 1;
          state.videoProgress = 0; // Сброс прогресса для нового видео
          console.log("Есть еще видео")
        } else {
          // Если это последнее видео в истории, закрываем историю
          state.currentStoryId = null;
          state.currentStory = null;
          state.currentVideoIndex = 0;
          state.videoProgress = 0;
          console.log("Нет истории 2")
        }
      } else {
        console.log("Нет истории")

        state.currentStoryId = null;
        state.currentStory = null;
        state.currentVideoIndex = 0;
        state.videoProgress = 0;
      }
    },
    onSwipeVideo: (state, action: PayloadAction<number>) => {
      const nextStoryId = state.currentStoryId !== null ? state.currentStoryId + action.payload : 0;
      if (nextStoryId >= 0 && nextStoryId < state.stories.length) {
        state.currentStoryId = nextStoryId;
        state.currentStory = state.stories[nextStoryId] || null;
        state.videoProgress = 0;
        state.currentVideoIndex = 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase("CLEANUP", (state) => {
      state.currentStoryId = null;
      state.currentStory = null;
      state.videoProgress = 0;
      state.currentVideoIndex = 0;
    });
    builder.addCase(loadBoxInterface.fulfilled, (state, action) => {
      state.stories = mapInterfaceStoriesToStories(action.payload.interfaceStories);
    });
  },
});

export const { showStory, onCloseStory, updateVideoProgress, onSwipeVideo, incrementVideoIndex } = storiesState.actions;

// Selectors
export const selectCurrentStory = (state: RootState) => state.stories.currentStory;
export const selectVideoProgress = (state: RootState) => state.stories.videoProgress;
export const selectStories = (state: RootState) => state.stories.stories;
export const selectCurrentVideoIndex = (state: RootState) => state.stories.currentVideoIndex;
export const selectCurrentVideoPercent = (state: RootState) => {
  if (state.stories.videoDuration == 0) {
    return 0
  }
  return state.stories.videoProgress / state.stories.videoDuration * 100;
}
export const selectCurrentVideoDuration = (state: RootState) => {
  return state.stories.videoDuration
}
export const selectCurrentVideo = (state: RootState) => {
  const currentStory = state.stories.currentStory;
  if (currentStory && currentStory.videos) {
    return currentStory.videos[state.stories.currentVideoIndex]
  }

  return null
};
export const selectCurrentStoryIcon = (state: RootState) => {
  const currentStory = state.stories.currentStory;
  if (currentStory && currentStory.videos) {
    return currentStory.image
  }

  return null
};

export const isLastVideo = (state: RootState) => {
  const currentStory = state.stories.currentStory;
  if (currentStory && currentStory.videos) {
    return currentStory.videos.length - 1 == state.stories.currentVideoIndex
  }
  return false
}

export default storiesState.reducer;

export interface VideoProgress {
  progress: number;
  duration: number;
}